/**
 * Scope-doc slash-menu scoping + the two capture commands.
 *
 * ONE rule governs the Scope doc: EXPLICIT commands capture into the questionnaire
 * (/problem = a new scope item, /functional-area = a new Business-Process section), while
 * GENERIC headings are pure doc content (user note sections, persisted doc-only). Functional
 * areas are strictly STRUCTURED - no formatting commands inside them, only /problem (removal is
 * the hover x affordance); free-form authoring (custom messages, follow-up notes) lives OUTSIDE
 * the areas with the full formatting set:
 *
 * | context                                   | h1-h3 | lists/quote | table/code/divider | /problem | /functional-area |
 * | doc-root, user-section                    |  yes  |     yes     |        yes         |    -     |       yes        |
 * | template section (incl. bodies/panels)    |   -   |     yes     |         -          |    -     |        -         |
 * | functional area + catch-all (incl. items) |   -   |      -      |         -          |   yes    |        -         |
 *
 * (formatting is hidden in captured zones because the harvest flattens it - blocks round-trip
 * verbatim only inside user sections.)
 */

import {
  ADHOC_GROUP_SECTION_PREFIX,
  ADHOC_SECTION_KEY,
  isAdhocGroupSectionKey,
} from '@protoss/shared';
import type { Editor, SlashCommandSpec, SlashMenuConfig } from '@protoss/ui';

import { parseScopeDocKey } from './scope-doc';

// ── Context classification ───────────────────────────────────────────────────

export type ScopeSlashContext =
  | { kind: 'doc-root' }
  | { kind: 'template-section' }
  | { kind: 'functional-area'; sectionKey: string; itemIndex: number | null }
  | { kind: 'adhoc-catch-all'; itemIndex: number | null }
  | { kind: 'user-section' };

/** The shape of a top-level doc block the classifier needs (pure JSON - unit-testable). */
export interface ScopeSlashBlock {
  type: string;
  level?: number | null;
  dataKey?: string | null;
  text?: string;
}

/**
 * Classify the caret's context from the top-level blocks: walk BACKWARDS from the caret's block
 * to the nearest H1 (the enclosing section - same "last H1 wins" rule the harvest applies),
 * remembering the nearest enclosing item heading (adhoc:/generic H2-H3) on the way.
 */
export function classifyScopeBlocks(
  blocks: readonly ScopeSlashBlock[],
  topIndex: number
): ScopeSlashContext {
  let itemIndex: number | null = null;
  for (let i = Math.min(topIndex, blocks.length - 1); i >= 0; i--) {
    const b = blocks[i]!;
    if (b.type !== 'heading') continue;
    const level = Number(b.level ?? 1);
    const anchor = parseScopeDocKey(b.dataKey);
    if (level === 1) {
      if (anchor?.type === 'section') {
        if (isAdhocGroupSectionKey(anchor.key)) {
          return { kind: 'functional-area', sectionKey: anchor.key, itemIndex };
        }
        if (anchor.key === ADHOC_SECTION_KEY) return { kind: 'adhoc-catch-all', itemIndex };
        return { kind: 'template-section' };
      }
      // A user note section: round-tripped (`user:` anchor) or freshly typed (no anchor).
      return { kind: 'user-section' };
    }
    if (itemIndex === null && (anchor?.type === 'adhoc' || (!anchor && (level === 2 || level === 3)))) {
      itemIndex = i;
    }
  }
  return { kind: 'doc-root' };
}

/** Minimal structural view of the live doc (typed loosely - apps/web has no tiptap dep). */
interface PmNodeLike {
  type: { name: string };
  attrs?: Record<string, unknown>;
  textContent: string;
  nodeSize: number;
}
interface PmDocLike {
  content: { size: number };
  childCount: number;
  forEach: (fn: (node: PmNodeLike, offset: number, index: number) => void) => void;
  resolve: (pos: number) => { index: (depth: number) => number };
}

function docOf(editor: Editor): PmDocLike {
  return editor.state.doc as unknown as PmDocLike;
}

function topBlocks(doc: PmDocLike): Array<ScopeSlashBlock & { pos: number; nodeSize: number }> {
  const out: Array<ScopeSlashBlock & { pos: number; nodeSize: number }> = [];
  doc.forEach((node, offset) => {
    out.push({
      type: node.type.name,
      level: (node.attrs?.level as number | undefined) ?? null,
      dataKey: (node.attrs?.dataKey as string | null | undefined) ?? null,
      text: node.textContent,
      pos: offset,
      nodeSize: node.nodeSize,
    });
  });
  return out;
}

/** The caret's context in the live editor doc. */
export function resolveScopeSlashContext(editor: Editor, pos: number): ScopeSlashContext {
  const doc = docOf(editor);
  const clamped = Math.max(0, Math.min(pos, doc.content.size));
  return classifyScopeBlocks(topBlocks(doc), doc.resolve(clamped).index(0));
}

// ── Insertion targets ────────────────────────────────────────────────────────

const ITEM_PLACEHOLDER = 'New scope item';
const AREA_PLACEHOLDER = 'New functional area';

/**
 * End of the section opened by the heading at block `i` (level from that heading): the position
 * just before the next heading of the same-or-higher level, else doc end.
 */
function sectionEndPos(
  blocks: ReadonlyArray<ScopeSlashBlock & { pos: number; nodeSize: number }>,
  i: number,
  docSize: number
): number {
  const level = Number(blocks[i]!.level ?? 1);
  for (let j = i + 1; j < blocks.length; j++) {
    const b = blocks[j]!;
    if (b.type === 'heading' && Number(b.level ?? 1) <= level) return b.pos;
  }
  return docSize;
}

/** Insert nodes at `pos`, select the placeholder heading text for immediate rename, scroll there. */
function insertAndRename(
  editor: Editor,
  pos: number,
  nodes: Array<Record<string, unknown>>,
  placeholder: string
): void {
  editor
    .chain()
    .focus()
    .insertContentAt(pos, nodes)
    .setTextSelection({ from: pos + 1, to: pos + 1 + placeholder.length })
    .scrollIntoView()
    .run();
}

/**
 * /problem - insert a captured scope item (H2, null dataKey -> the harvest's ParsedNewItem path).
 * Only offered INSIDE a functional area / the legacy catch-all: after the current item's section
 * (never mid-item), or right after the caret's block when between items. Defensive fallback when
 * invoked elsewhere (e.g. a stale menu): append to the LAST area; no area at all = no-op.
 */
export function insertProblemItem(editor: Editor): void {
  const doc = docOf(editor);
  const blocks = topBlocks(doc);
  const selFrom = (editor.state.selection as unknown as { from: number }).from;
  const ctx = resolveScopeSlashContext(editor, selFrom);
  const docSize = doc.content.size;

  let pos: number;
  if (ctx.kind === 'functional-area' || ctx.kind === 'adhoc-catch-all') {
    if (ctx.itemIndex != null) {
      // Inside an item's body (or on its heading): insert after that item's section.
      pos = sectionEndPos(blocks, ctx.itemIndex, docSize);
    } else {
      // Between items: right after the caret's top-level block.
      const topIndex = doc.resolve(Math.max(0, Math.min(selFrom, docSize))).index(0);
      const cur = blocks[topIndex];
      pos = cur ? cur.pos + cur.nodeSize : docSize;
    }
  } else {
    // Not inside an area: append to the END of the last ad-hoc section, if any.
    let lastAreaIdx = -1;
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i]!;
      if (b.type !== 'heading' || Number(b.level ?? 1) !== 1) continue;
      const anchor = parseScopeDocKey(b.dataKey);
      if (
        anchor?.type === 'section' &&
        (isAdhocGroupSectionKey(anchor.key) || anchor.key === ADHOC_SECTION_KEY)
      ) {
        lastAreaIdx = i;
      }
    }
    if (lastAreaIdx < 0) return;
    pos = sectionEndPos(blocks, lastAreaIdx, docSize);
  }

  insertAndRename(
    editor,
    pos,
    [
      {
        type: 'heading',
        attrs: { level: 2 },
        content: [{ type: 'text', text: ITEM_PLACEHOLDER }],
      },
      { type: 'paragraph' },
    ],
    ITEM_PLACEHOLDER
  );
}

/**
 * /functional-area - insert a captured Business-Process section: an H1 pre-stamped with a fresh
 * ad-hoc group anchor (so the harvest's sectionBp picks its TEXT up as the BP name) plus a
 * placeholder first item (new items under it inherit the BP; an empty area has nowhere to persist
 * and would vanish on rebuild). Placed at the BOTTOM of the model sections - before the first user
 * note section when one exists, else at the doc end - matching where the rebuilt doc puts it.
 */
export function insertFunctionalArea(editor: Editor): void {
  const doc = docOf(editor);
  const blocks = topBlocks(doc);
  const firstUserIdx = blocks.findIndex(
    (b) =>
      b.type === 'heading' &&
      Number(b.level ?? 1) === 1 &&
      (parseScopeDocKey(b.dataKey)?.type === 'user' || !b.dataKey)
  );
  const pos = firstUserIdx >= 0 ? blocks[firstUserIdx]!.pos : doc.content.size;
  // Random suffix: two unrenamed new areas must not collide on dataKey (drag + sectionBp key on it).
  const rand = Math.random().toString(36).slice(2, 8);

  insertAndRename(
    editor,
    pos,
    [
      {
        type: 'heading',
        attrs: {
          level: 1,
          dataKey: `section:${ADHOC_GROUP_SECTION_PREFIX}new_functional_area_${rand}`,
        },
        content: [{ type: 'text', text: AREA_PLACEHOLDER }],
      },
      {
        type: 'heading',
        attrs: { level: 2 },
        content: [{ type: 'text', text: ITEM_PLACEHOLDER }],
      },
      { type: 'paragraph' },
    ],
    AREA_PLACEHOLDER
  );
}

// ── Menu builder ─────────────────────────────────────────────────────────────

const TEXT_COMMANDS = ['bullet', 'ordered', 'quote'] as const;

const PROBLEM_COMMAND: SlashCommandSpec = {
  id: 'problem',
  label: 'Problem',
  description: 'New scope item (captured in the questionnaire)',
  keywords: ['problem', 'scope', 'item', 'question'],
  action: insertProblemItem,
};

const FUNCTIONAL_AREA_COMMAND: SlashCommandSpec = {
  id: 'functional-area',
  label: 'Functional area',
  description: 'New business-process section (captured in the questionnaire)',
  keywords: ['functional', 'area', 'section', 'business', 'process'],
  action: insertFunctionalArea,
};

/** The Scope doc's SlashMenuCustomizer - pass to RichTextEditor.customizeSlashMenu. */
export function buildScopeSlashMenu(args: { editor: Editor; triggerPos: number }): SlashMenuConfig {
  const ctx = resolveScopeSlashContext(args.editor, args.triggerPos);
  switch (ctx.kind) {
    // Free zone (notes, follow-up questions, custom messages): full formatting + area creation.
    case 'doc-root':
    case 'user-section':
      return { allow: null, extra: [FUNCTIONAL_AREA_COMMAND] };
    case 'template-section':
      return { allow: TEXT_COMMANDS, extra: [] };
    // Functional areas are STRUCTURED: no formatting - adding a question is the only command.
    case 'functional-area':
    case 'adhoc-catch-all':
      return { allow: [], extra: [PROBLEM_COMMAND] };
  }
}
